import { ModalButton } from '@/components/common/modal-button';
import { NearbyPoiItem } from '@/components/feature/floor-guide/nearby-poi-item';
import { RouteGuideIcon } from '@/components/icons';
import { StopIcon } from '@/components/icons/floor-guide';
import { POI_ID_DELIMITER } from '@/constants/floor-guide';
import { useAccessibilityTTS, useTickSoundInteraction } from '@/hooks';
import { useConfigStore, useModalStackStore } from '@/stores';
import type { NearbyPoiItemWithDistanceType } from '@/types/map';
import { generateFloorName } from '@/utils/floor-guide';
import clsx from 'clsx';
import { memo, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MODAL_IDS } from '@/constants/modal-stack';
import { useFocusTrap } from '@/hooks/modal';
import styles from './styles.module.scss';

type Props = {
    poiList: NearbyPoiItemWithDistanceType[];
    selectedPoiId?: string;
    onSelectPoi: (poi: NearbyPoiItemWithDistanceType) => void;
    onStartNavigationGuide: (poi: NearbyPoiItemWithDistanceType) => void;
    onClose: () => void;
    isActive: boolean;
    title: string;
    floorName: string;
    buildingName: string;
    type?: 'category' | 'show-location';
    trapFocus?: boolean;
};

export const NearbyPoiListDropdown = memo(
    ({ poiList, selectedPoiId, onSelectPoi, onStartNavigationGuide, onClose, isActive, title, floorName, buildingName, type = 'category', trapFocus = true }: Props) => {
        const nearbyPoiListDropdownRef = useRef<HTMLDivElement>(null);
        const { t } = useTranslation();
        const language = useConfigStore((state) => state.config?.ui?.language);
        const buildings = useConfigStore((state) => state?.config?.server?.store?.buildings);
        const { playTick } = useTickSoundInteraction();
        const idModal = MODAL_IDS.NEARBY_POI_LIST;

         const { pushModal, popModal, isModalInStack } = useModalStackStore();

        const { speak } = useAccessibilityTTS();

        const allPoiItems = useMemo(() => {
            if (!buildings) {
                return [];
            }
            return buildings.flatMap((item) => (Array.isArray(item?.poiContents) ? item?.poiContents : []));
        }, [buildings]);

        const getPoiItemByStudioPoiId = useCallback(
            (studioPoiId: string) => {
                if (!Array.isArray(allPoiItems) || !allPoiItems?.length) {
                    return undefined;
                }

                return allPoiItems.find((item) => item?.studioPoiId === studioPoiId);
            },
            [allPoiItems]
        );

        const handleStartNavigationGuide = () => {
            if (type === 'show-location' && Array.isArray(poiList) && poiList.length) {
                onStartNavigationGuide(poiList[0]);
                return;
            }

            const poiItem = poiList.find((poi) => `${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}` === selectedPoiId);
            if (!poiItem) {
                return;
            }

            onStartNavigationGuide(poiItem);
        };

        useEffect(() => {
            if (!trapFocus) return;

            if (isActive) {
                if (!isModalInStack(idModal)) {
                    pushModal(idModal);
                }
            } else {
                if (isModalInStack(idModal)) {
                    popModal(idModal);
                }
            }

            return () => {
                if (isModalInStack(idModal)) {
                    popModal(idModal);
                }
            };
        }, [isActive, idModal, pushModal, popModal, isModalInStack, trapFocus]);

        useFocusTrap(nearbyPoiListDropdownRef, isActive, onClose, false, idModal, false, false);

        const headerTitle = `${title}, ${Array.isArray(buildings) && buildings.length && buildings.length > 1 ? buildingName : ''}, ${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) })}`;

        return (
            <div className={styles['wrapper']} ref={nearbyPoiListDropdownRef} role="dialog" aria-modal="true">
                <div
                    className={clsx(styles['popup__header'], 'focus-border')}
                    data-interactive
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    aria-label={t('floor_guide.button.location_confirmation_tts')}
                    data-modal-region
                >
                    <h3 className={clsx(styles['popup__header__title'], 'text-text-main01 line-clamp-1 text-ellipsis')}>
                        {t('floor_guide.button.location_confirmation')}
                    </h3>
                </div>
                <div className={clsx(styles['header'], 'focus-border')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    data-interactive tabIndex={0} aria-label={headerTitle} data-modal-region>
                    <p className={clsx(styles['header__title'], 'line-clamp-1 text-ellipsis')}>{title}</p>
                    <span className={styles['header__building-floor']}>
                        {Array.isArray(buildings) && buildings.length && buildings.length > 1 && (
                            <span className={styles['header__building-floor__building']}>{buildingName}</span>
                        )}
                        <span className={styles['header__building-floor__floor']}>{floorName}</span>
                    </span>
                </div>
                <div className={clsx(styles['nearby-poi-list'], 'scrollbar-custom-gray-99938f scrollbar-w-4')}>
                    {poiList.map((poi, index) => {
                        const nearbyPoiId = poi?.nearbyPoi?.id;
                        if (!nearbyPoiId) {
                            return null;
                        }
                        const poiItem = getPoiItemByStudioPoiId(nearbyPoiId);

                        if (!poiItem) {
                            return null;
                        }

                        return (
                            <NearbyPoiItem
                                selectedPoiId={selectedPoiId}
                                key={poi?.id}
                                dataModalRegion={index === 0}
                                name={poiItem?.name?.[language] || poiItem?.name?.['ko'] || ''}
                                selected={`${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}` === selectedPoiId}
                                onSelect={() => onSelectPoi(poi)}
                            />
                        );
                    })}
                </div>
                <div className={styles['actions']}>
                    <ModalButton
                        data-modal-region
                        size="sm"
                        icon={<RouteGuideIcon/>}
                        tabIndex={0}
                        role="button"
                        data-interactive
                        aria-label={t('floor_guide.button.direction_guide')}
                        data-role={t('common.data_role.button')}
                        onClick={handleStartNavigationGuide}
                    >
                        {t('floor_guide.button.direction_guide')}
                    </ModalButton>
                    <ModalButton
                        data-tts-scope="nearby-poi-list-button"
                        data-modal-region
                        size="sm"
                        icon={<StopIcon />}
                        variant="secondary"
                        tabIndex={0}
                        role="button"
                        data-interactive
                        data-role={t('common.data_role.button')}
                        onMouseDown={(e) => {
                            if (e.detail !== 0) {
                                e.preventDefault();
                            }
                        }}
                        onClick={() => {
                            speak(t('floor_guide.text_to_speech.location_confirm_has_been_completed'));
                            onClose();
                        }}
                        onFocus={() => {
                            speak(`${t('floor_guide.button.stop')} ${t('common.data_role.button')}`);
                        }}
                    >
                        {t('floor_guide.button.stop')}
                    </ModalButton>
                </div>
            </div>
        );
    }
);

NearbyPoiListDropdown.displayName = 'NearbyPoiListDropdown';
